import {
  type AcademicTerm,
  compareStudentNumbers,
  type Student,
  studentNumberLabel,
  type TermRecordSource,
  termStudentSummaries,
} from "@classroom/domain";
import { Fragment, useMemo, useState } from "react";
import GradeCategory from "./GradeCategory";
import { useTranslation } from "./i18n";
import type { RecordExportScope } from "./lib/localRecordExport";
import RecordExportButton from "./RecordExportButton";
import StudentPagination, { useStudentPagination } from "./StudentPagination";
import { ToolPageToolbar } from "./ToolPageHeader";

type Period = ReturnType<typeof termStudentSummaries>[number]["periods"][number];
type Metric = "grade" | "answers" | "materials" | "attendance" | "subjectHomework" | "homeroomHomework";
const valueFor = (row: Period, metric: Metric): number | undefined => {
  if (metric === "grade") return row.average;
  if (metric === "answers") return row.responseRate;
  if (metric === "materials") return row.materialRate;
  if (metric === "attendance") return row.attendanceRate;
  if (metric === "subjectHomework")
    return row.subjectRecorded ? ((row.subjectMissing || 0) / row.subjectRecorded) * 100 : undefined;
  if (metric === "homeroomHomework")
    return row.homeroomTasks ? ((row.homeroomMissing || 0) / row.homeroomTasks) * 10 : undefined;
  return undefined;
};
const decimal = (value: number) => String(Math.round(value * 10) / 10);

export default function TermOverview({
  terms,
  selectedId,
  students,
  classId,
  className,
  classRole,
  hiddenTools = [],
  source,
  onSelect,
  onManage,
  onExport,
}: {
  terms: AcademicTerm[];
  selectedId: string;
  students: Student[];
  classId: string;
  className: string;
  classRole: "teaching" | "homeroom";
  hiddenTools?: string[];
  source: TermRecordSource;
  onSelect: (id: string) => void;
  onManage: () => void;
  onExport: (scope: RecordExportScope) => void;
}) {
  const { t } = useTranslation("terms");
  const ordered = [...terms].sort((a, b) => a.startDate.localeCompare(b.startDate));
  const selected =
    ordered.find((term) => term.id === selectedId) ||
    [...ordered]
      .reverse()
      .find((term) => term.startDate <= new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Hong_Kong" })) ||
    ordered[0];
  const [compareId, setCompareId] = useState<string>();
  const defaultPrevious = ordered[ordered.findIndex((term) => term.id === selected?.id) - 1];
  const previous = ordered.find(
    (term) => term.id === (compareId === undefined ? defaultPrevious?.id : compareId) && term.id !== selected?.id,
  );
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("number");
  const [category, setCategory] = useState("");
  const showGrades = classRole === "teaching" && Boolean(category);
  const filteredSource = useMemo(() => {
    if (classRole !== "teaching" || !category) return source;
    const assignments = source.homeworkAssignments.filter(
      (assignment) => (assignment.category || "homework") === category,
    );
    const ids = new Set(assignments.map((assignment) => assignment.id));
    return {
      ...source,
      homeworkAssignments: assignments,
      homeworkRecords: source.homeworkRecords.filter((record) => ids.has(record.assignmentId)),
    };
  }, [classRole, category, source]);
  const { t: gradeCopy } = useTranslation("gradeOverview");
  const metricLabel = (metric: Metric) =>
    metric === "subjectHomework"
      ? t(category === "homework" ? "homeworkMissing" : category ? "examMissing" : "recordMissing")
      : t(metric);
  const categoryLabel = gradeCopy(
    `categories.${category === "homework" || category === "assessment" || category === "dictation" ? category : "all"}`,
  );
  const [expanded, setExpanded] = useState<string>();
  const summaries = useMemo(
    () => termStudentSummaries(terms, classId, students, filteredSource),
    [terms, classId, students, filteredSource],
  );
  const metrics: Metric[] =
    classRole === "teaching"
      ? showGrades
        ? ["grade", "subjectHomework"]
        : ["subjectHomework"]
      : [
          ...(!hiddenTools.includes("attendance") ? ["attendance" as const] : []),
          ...(!hiddenTools.includes("homeroomHomework") ? ["homeroomHomework" as const] : []),
        ];
  const currentFor = (row: (typeof summaries)[number]) =>
    row.periods.find((period) => period.term.id === selected?.id)!;
  const previousFor = (row: (typeof summaries)[number]) =>
    row.periods.find((period) => period.term.id === previous?.id);
  const diff = (row: (typeof summaries)[number], metric: Metric) => {
    const before = previousFor(row);
    const current = currentFor(row);
    const a = current && valueFor(current, metric);
    const b = before && valueFor(before, metric);
    return a === undefined || b === undefined ? undefined : a - b;
  };
  const followup = (row: (typeof summaries)[number]) => {
    const current = currentFor(row);
    if (classRole === "teaching") return current?.subjectMissing || 0;
    return current
      ? (metrics.includes("homeroomHomework") ? current.homeroomMissing || 0 : 0) +
          (metrics.includes("attendance") ? (current.absent || 0) + (current.late || 0) : 0)
      : 0;
  };
  const visible = summaries
    .filter(({ student }) =>
      `${studentNumberLabel(student)} ${student.name}`.toLocaleLowerCase().includes(search.trim().toLocaleLowerCase()),
    )
    .sort(
      (a, b) =>
        (sort === "grade"
          ? (diff(b, "grade") ?? -Infinity) - (diff(a, "grade") ?? -Infinity)
          : sort === "answers"
            ? (diff(b, "answers") ?? -Infinity) - (diff(a, "answers") ?? -Infinity)
            : sort === "followup"
              ? followup(b) - followup(a)
              : 0) || compareStudentNumbers(a.student, b.student),
    );
  const pagination = useStudentPagination(
    visible,
    JSON.stringify([classId, selected?.id, previous?.id, category, sort, search]),
  );
  function mainValue(row: Period, metric: Metric) {
    const value = valueFor(row, metric);
    if (value === undefined) return t("missing");
    if (metric === "answers") return t("perDay", { value: decimal(value) });
    if (metric === "homeroomHomework") return t("perTen", { value: decimal(value) });
    return t("percent", { value: decimal(value) });
  }
  function note(row: Period, metric: Metric) {
    if (valueFor(row, metric) === undefined) return "";
    if (metric === "grade") return t("gradeCount", { value: row.scored });
    if (metric === "answers" || metric === "materials")
      return t("dailyCount", {
        days: row.observedDays,
        value: metric === "answers" ? row.answers || 0 : row.materials || 0,
      });
    if (metric === "attendance")
      return t("attendanceCount", { days: row.attendanceDays, absent: row.absent || 0, late: row.late || 0 });
    if (metric === "subjectHomework")
      return t(category === "homework" ? "taskCount" : category ? "examCount" : "recordCount", {
        tasks: row.subjectRecorded,
        value: row.subjectMissing || 0,
      });
    if (metric === "homeroomHomework")
      return t("homeroomCount", { tasks: row.homeroomTasks, value: row.homeroomMissing || 0 });
    return "";
  }
  function change(row: Period, baseline: Period | undefined, metric: Metric) {
    if (valueFor(row, metric) === undefined) return <span className="term-change neutral">{t("noCurrent")}</span>;
    if (!baseline) return null;
    const value = valueFor(row, metric)!;
    const old = valueFor(baseline, metric);
    if (old === undefined) return <span className="term-change neutral">{t("noBaseline")}</span>;
    const delta = Math.round((value - old) * 10) / 10;
    const unit = t(metric === "answers" ? "perDayUnit" : metric === "homeroomHomework" ? "perTenUnit" : "points", {
      value: "",
    });
    const favorable = ["grade", "answers", "attendance"].includes(metric) ? delta > 0 : delta < 0;
    return (
      <span className={`term-change ${!delta ? "neutral" : favorable ? "improved" : "attention"}`}>
        {delta
          ? `${delta > 0 ? "↑" : "↓"} ${t(delta > 0 ? "up" : "down", { value: decimal(Math.abs(delta)), unit })}`
          : t("same")}
      </span>
    );
  }
  const monthly = useMemo(() => {
    if (!selected) return [];
    const periods: AcademicTerm[] = [];
    let cursor = `${selected.startDate.slice(0, 7)}-01`;
    while (cursor <= selected.endDate && periods.length < 120) {
      const next = new Date(`${cursor}T00:00:00Z`);
      next.setUTCMonth(next.getUTCMonth() + 1);
      const end = new Date(next.getTime() - 86400_000).toISOString().slice(0, 10);
      periods.push({
        ...selected,
        id: cursor,
        name: cursor.slice(0, 7),
        startDate: cursor < selected.startDate ? selected.startDate : cursor,
        endDate: end > selected.endDate ? selected.endDate : end,
      });
      cursor = next.toISOString().slice(0, 10);
    }
    return termStudentSummaries(periods, classId, students, filteredSource);
  }, [selected, classId, students, filteredSource]);
  function output(intent?: "print") {
    if (!selected) return;
    const [headers, ...rows] = [
      [
        t("student"),
        t("schoolYear"),
        t("selected"),
        t("compare"),
        ...(classRole === "teaching" ? [t("category")] : []),
        ...metrics.flatMap((metric) => [
          metricLabel(metric),
          `${metricLabel(metric)}（${previous?.name || "—"}）`,
          `${metricLabel(metric)}（${t("change")}）`,
        ]),
      ],
      ...visible.map((row) => [
        `${studentNumberLabel(row.student)} ${row.student.name}`,
        selected.schoolYear,
        selected.name,
        previous ? `${previous.schoolYear} ${previous.name}` : "",
        ...(classRole === "teaching" ? [categoryLabel] : []),
        ...metrics.flatMap((metric) => [
          mainValue(currentFor(row), metric),
          previousFor(row) ? mainValue(previousFor(row)!, metric) : "",
          diff(row, metric) === undefined ? "" : decimal(diff(row, metric)!),
        ]),
      ]),
    ];
    const printTitle = `${selected.schoolYear} ${selected.name}｜${t("title")}${classRole === "teaching" ? `｜${categoryLabel}` : ""}｜${selected.startDate}–${selected.endDate}`;
    onExport({
      intent,
      printReport: {
        title: printTitle,
        className,
        sheets: [
          {
            title: t("title"),
            headers: [
              t("student"),
              ...metrics.flatMap((metric) => [
                metricLabel(metric),
                ...(previous
                  ? [
                      `${metricLabel(metric)}（${previous.schoolYear} ${previous.name}）`,
                      `${metricLabel(metric)}（${t("change")}）`,
                    ]
                  : []),
              ]),
            ],
            rows: visible.map((row) => [
              `${studentNumberLabel(row.student)} ${row.student.name}`,
              ...metrics.flatMap((metric) => [
                `${mainValue(currentFor(row), metric)}${note(currentFor(row), metric) ? ` · ${note(currentFor(row), metric)}` : ""}`,
                ...(previous
                  ? [
                      previousFor(row) ? mainValue(previousFor(row)!, metric) : "",
                      diff(row, metric) === undefined ? "" : decimal(diff(row, metric)!),
                    ]
                  : []),
              ]),
            ]),
          },
        ],
      },
      report: {
        title: `${selected.schoolYear} ${selected.name}｜${t("title")}${classRole === "teaching" ? `｜${categoryLabel}` : ""}`,
        className,
        sheets: [{ title: t("title"), headers, rows }],
      },
      label: t("title"),
    });
  }
  if (!selected)
    return (
      <section className="paper-card term-overview-empty">
        <h3>{t("title")}</h3>
        <p>{t("noTerms")}</p>
        <button className="primary-action" type="button" onClick={onManage}>
          {t("configure")}
        </button>
      </section>
    );
  return (
    <div className="term-overview" aria-label={t("title")}>
      <h3 className="sr-only">{t("title")}</h3>
      <ToolPageToolbar
        scope={
          <div className="term-comparison-controls">
            {classRole === "teaching" && (
              <GradeCategory
                label={t("category")}
                value={category}
                onChange={(value) => {
                  setCategory(value);
                  setSort("number");
                }}
              />
            )}
            <label>
              {t("selected")}
              <select
                value={selected.id}
                onChange={(event) => {
                  onSelect(event.target.value);
                  setCompareId(undefined);
                }}
              >
                {ordered.map((term) => (
                  <option value={term.id} key={term.id}>
                    {term.schoolYear} · {term.name}
                  </option>
                ))}
              </select>
            </label>
            <span aria-hidden="true">↔</span>
            <label>
              {t("compare")}
              <select value={previous?.id || ""} onChange={(event) => setCompareId(event.target.value)}>
                <option value="">{t("noCompare")}</option>
                {ordered
                  .filter((term) => term.id !== selected.id)
                  .map((term) => (
                    <option value={term.id} key={term.id}>
                      {term.schoolYear} · {term.name}
                    </option>
                  ))}
              </select>
            </label>
            <small>{t("range", { start: selected.startDate, end: selected.endDate })}</small>
          </div>
        }
        actions={
          <RecordExportButton onClick={() => output()} onPrint={() => output("print")} disabled={!metrics.length} />
        }
      />
      {metrics.length > 0 && (
        <div className="term-summary-stats">
          <section className="paper-card">
            <span>{t("studentsCount", { value: students.length })}</span>
            <strong>{selected.name}</strong>
          </section>
          {showGrades && (
            <>
              <section className="paper-card">
                <span>{t("gradeUp")}</span>
                <strong>
                  {summaries.filter((row) => (diff(row, "grade") ?? 0) > 0).length}
                  <small> / {summaries.filter((row) => diff(row, "grade") !== undefined).length}</small>
                </strong>
                <small>{t("comparable")}</small>
              </section>
            </>
          )}
          <section className="paper-card">
            <span>
              {t(
                classRole === "teaching"
                  ? category === "homework"
                    ? "followup"
                    : category
                      ? "examFollowup"
                      : "recordFollowup"
                  : "homeroomFollowup",
              )}
            </span>
            <strong>{summaries.filter((row) => followup(row) > 0).length}</strong>
          </section>
        </div>
      )}
      <section className="paper-card term-students">
        <header>
          <h3>
            {previous
              ? t("compared", { current: selected.name, previous: `${previous.schoolYear} ${previous.name}` })
              : selected.name}
          </h3>
          <div className="term-student-controls">
            <input
              aria-label={t("search")}
              placeholder={t("searchPlaceholder")}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            <select aria-label={t("sort")} value={sort} onChange={(event) => setSort(event.target.value)}>
              <option value="number">{t("byNumber")}</option>
              {showGrades && (
                <>
                  <option value="grade">{t("byGrade")}</option>
                </>
              )}
              {metrics.length > 0 && <option value="followup">{t("byFollowup")}</option>}
            </select>
          </div>
        </header>
        {/* biome-ignore lint/a11y/noNoninteractiveTabindex: Keyboard focus supports horizontal table scrolling on narrow screens. */}
        <div className="term-table-scroll" role="region" aria-label={t("title")} tabIndex={0}>
          <table className="term-table">
            <thead>
              <tr>
                <th scope="col">{t("student")}</th>
                {metrics.map((metric) => (
                  <th scope="col" key={metric}>
                    {metricLabel(metric)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pagination.items.map((row) => (
                <Fragment key={row.student.id}>
                  <tr>
                    <th scope="row">
                      <button
                        type="button"
                        className="grade-student-button"
                        aria-label={t("expand", { number: studentNumberLabel(row.student), name: row.student.name })}
                        aria-expanded={expanded === row.student.id}
                        aria-controls={`term-detail-${row.student.id}`}
                        onClick={() => setExpanded(expanded === row.student.id ? undefined : row.student.id)}
                      >
                        <span>{studentNumberLabel(row.student)}</span>
                        <b>{row.student.name}</b>
                        <i aria-hidden="true">{expanded === row.student.id ? "−" : "+"}</i>
                      </button>
                      {!row.student.active && <small>{t("disabled")}</small>}
                    </th>
                    {metrics.map((metric) => (
                      <td key={metric}>
                        <strong>{mainValue(currentFor(row), metric)}</strong>
                        <small>{note(currentFor(row), metric)}</small>
                        {change(currentFor(row), previousFor(row), metric)}
                      </td>
                    ))}
                  </tr>
                  {expanded === row.student.id && (
                    <tr>
                      <td colSpan={metrics.length + 1}>
                        <section
                          className="term-detail"
                          id={`term-detail-${row.student.id}`}
                          aria-label={t("detailTitle", {
                            number: studentNumberLabel(row.student),
                            name: row.student.name,
                          })}
                        >
                          <h4>{t("history")}</h4>
                          <table aria-label={t("history")}>
                            <thead>
                              <tr>
                                <th scope="col">{t("selected")}</th>
                                {metrics.map((metric) => (
                                  <th scope="col" key={metric}>
                                    {metricLabel(metric)}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {row.periods.map((period) => (
                                <tr key={period.term.id}>
                                  <th scope="row">
                                    {period.term.schoolYear}
                                    <br />
                                    {period.term.name}
                                  </th>
                                  {metrics.map((metric) => (
                                    <td key={metric}>
                                      {mainValue(period, metric)}
                                      <small>{note(period, metric)}</small>
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          <h4>{t("monthly")}</h4>
                          <table aria-label={t("monthly")}>
                            <thead>
                              <tr>
                                <th scope="col">{t("month")}</th>
                                {metrics.map((metric) => (
                                  <th scope="col" key={metric}>
                                    {metricLabel(metric)}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {monthly
                                .find((item) => item.student.id === row.student.id)
                                ?.periods.map((period) => (
                                  <tr key={period.term.id}>
                                    <th scope="row">{period.term.name}</th>
                                    {metrics.map((metric) => (
                                      <td key={metric}>
                                        {mainValue(period, metric)}
                                        <small>{note(period, metric)}</small>
                                      </td>
                                    ))}
                                  </tr>
                                ))}
                            </tbody>
                          </table>
                        </section>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
        <StudentPagination {...pagination} />
        {!visible.length && <p>{t("empty")}</p>}
      </section>
    </div>
  );
}
